import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
	Popover,
	Box,
	Typography,
	TextField,
	InputAdornment,
	FormControlLabel,
	Checkbox,
	Button,
	Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// AG-Grid Set Filter clone. Pending selection is local to the popover; nothing leaves until
// onApply fires. onApply runs both for the explicit Apply button and for close-by-outside-click /
// Esc, so the per-dimension UX is "apply on close" (matches the apply behavior the user picked).
// Reset clears the pending list in place — the user can still keep tweaking before applying.
export default function SetFilterPopover({
	open,
	anchorEl,
	title,
	options,
	selected,
	onApply,
}) {
	const [pending, setPending] = useState(selected || []);
	const [searchText, setSearchText] = useState('');

	// Sync local pending state to the committed selection each time the popover opens. Resetting
	// search at the same time so a stale query doesn't filter the new option list.
	useEffect(() => {
		if (open) {
			setPending(Array.isArray(selected) ? selected : []);
			setSearchText('');
		}
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	const normalizedOptions = useMemo(() => {
		const seen = new Set();
		const out = [];
		(options || []).forEach((opt) => {
			let value;
			let label;
			if (opt == null) return;
			if (typeof opt === 'string') {
				value = opt;
				label = opt;
			} else if (typeof opt === 'object') {
				value = opt.itemName ?? opt.value ?? opt.id;
				label = opt.itemName ?? opt.label ?? opt.value ?? String(opt.id ?? '');
			}
			if (value == null || value === '' || seen.has(value)) return;
			seen.add(value);
			out.push({ value: String(value), label: String(label) });
		});
		return out;
	}, [options]);

	const filteredOptions = useMemo(() => {
		const q = searchText.trim().toLowerCase();
		if (!q) return normalizedOptions;
		return normalizedOptions.filter((o) => o.label.toLowerCase().includes(q));
	}, [normalizedOptions, searchText]);

	const pendingSet = useMemo(() => new Set(pending), [pending]);
	const visibleValues = useMemo(() => filteredOptions.map((o) => o.value), [filteredOptions]);
	const allVisibleChecked = filteredOptions.length > 0 && visibleValues.every((v) => pendingSet.has(v));
	const someVisibleChecked = !allVisibleChecked && visibleValues.some((v) => pendingSet.has(v));

	// Guard against re-firing onApply during the unmount transition. Without this, the Popover's
	// onClose can fire AFTER the parent has already swapped activeDim and we'd commit empty state
	// to whatever the new active dimension happens to be.
	const appliedRef = useRef(false);
	useEffect(() => {
		if (open) appliedRef.current = false;
	}, [open]);

	const commit = (next) => {
		if (appliedRef.current) return;
		appliedRef.current = true;
		onApply(next);
	};

	const handleClose = () => commit(pending);
	const handleApply = () => commit(pending);
	const handleReset = () => setPending([]);

	const toggle = (value) => {
		setPending((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
	};

	const toggleAll = () => {
		if (allVisibleChecked) {
			setPending((prev) => prev.filter((v) => !visibleValues.includes(v)));
		} else {
			setPending((prev) => Array.from(new Set([...prev, ...visibleValues])));
		}
	};

	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={handleClose}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			slotProps={{
				paper: {
					sx: {
						mt: 0.5,
						width: 280,
						boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
						borderRadius: 2,
					},
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins' }}>
				<Box sx={{ px: 2, py: 1.25 }}>
					<Typography sx={{ fontWeight: 600, fontSize: 13, color: '#333' }}>{title}</Typography>
				</Box>
				<Divider />
				<Box sx={{ p: 1 }}>
					<TextField
						size="small"
						fullWidth
						autoFocus
						placeholder="Search..."
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon sx={{ fontSize: 18, color: '#999' }} />
								</InputAdornment>
							),
							sx: { fontFamily: 'Poppins', fontSize: 13 },
						}}
					/>
				</Box>
				<Divider />
				<FormControlLabel
					sx={{
						width: '100%',
						ml: 0,
						pl: 1,
						py: 0,
						'&:hover': { backgroundColor: '#F7F9FC' },
					}}
					control={
						<Checkbox
							size="small"
							checked={allVisibleChecked}
							indeterminate={someVisibleChecked}
							onChange={toggleAll}
							sx={{ p: 0.5 }}
						/>
					}
					label={
						<Typography sx={{ fontSize: 13, fontStyle: 'italic', color: '#555', fontFamily: 'Poppins' }}>
							(Select All)
						</Typography>
					}
				/>
				<Divider />
				<Box sx={{ maxHeight: 260, overflowY: 'auto', overflowX: 'hidden' }}>
					{filteredOptions.length === 0 ? (
						<Box sx={{ p: 2, textAlign: 'center', color: '#999', fontSize: 13 }}>
							No matches
						</Box>
					) : (
						filteredOptions.map((opt) => (
							<FormControlLabel
								key={opt.value}
								sx={{
									width: '100%',
									ml: 0,
									pl: 1,
									py: 0,
									'&:hover': { backgroundColor: '#F7F9FC' },
								}}
								control={
									<Checkbox
										size="small"
										checked={pendingSet.has(opt.value)}
										onChange={() => toggle(opt.value)}
										sx={{ p: 0.5 }}
									/>
								}
								label={
									<Typography
										sx={{
											fontSize: 13,
											fontFamily: 'Poppins',
											color: '#333',
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											maxWidth: 200,
										}}
										title={opt.label}
									>
										{opt.label}
									</Typography>
								}
							/>
						))
					)}
				</Box>
				<Divider />
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
					<Button
						size="small"
						onClick={handleReset}
						sx={{
							textTransform: 'none',
							fontFamily: 'Poppins',
							fontSize: 12,
							color: '#666',
						}}
					>
						Reset
					</Button>
					<Button
						size="small"
						variant="contained"
						onClick={handleApply}
						sx={{
							textTransform: 'none',
							fontFamily: 'Poppins',
							fontSize: 12,
							backgroundColor: '#3966D0',
							'&:hover': { backgroundColor: '#2E55B8' },
						}}
					>
						Apply
					</Button>
				</Box>
			</Box>
		</Popover>
	);
}
